import { Authsignal as NodeAuthSignal } from "@authsignal/node"
import { parse } from "next-useragent"

import { AppSettings } from "@/settings"

class _AuthsignalService {
  #config = {}

  /** @type {NodeAuthSignal} */
  #client = null

  messages = {
    BLOCK: "BLOCK",
    UNAUTHORIZED: "UNAUTHORIZED",
    VALID: "VALID",
    UNKNOWN: "UNKNOWN",
    PARAMS_MISSING: "PARAMS_MISSING",
    USER_ALREADY_ENROLLED: "USER_ALREADY_ENROLLED",
    SUCCESS: "SUCCESS",
    MISSING_TOKEN: "MISSING_TOKEN",
  }

  actions = {
    LOGIN: "login",
    ENROL: "enroll-passkey",
    SINGUP: "signup",
    ENROLL_REQUIRED: "enroll-required",
  }

  constructor() {
    this.#config = AppSettings.authsignalConfig()

    const { appUrl, apiKeySecret } = this.#config

    if (appUrl && apiKeySecret) {
      this.#client = new NodeAuthSignal({
        apiSecretKey: apiKeySecret,
        apiUrl: appUrl,
      })
    }
  }

  client() {
    return this.#client
  }

  /** @returns {boolean}  */
  mfaDisabled() {
    return this.#config.mfaDisabled
  }

  /**
   * Checks if MFA is required for the given IP
   *
   * @param {string} ip
   * @returns {boolean}
   */
  isMFAIgnored(ip) {
    const { mfaDisabled, mfaWhitelistedIps } = this.#config

    if (mfaDisabled) return true

    return mfaWhitelistedIps?.includes(ip)
  }

  /**
   * Extracts the information from user agent
   *
   * @param {string} userAgent
   * @returns {{browserName: string, browserVersion: string, deviceVendor: string, deviceModel: string, deviceType: string, osName: string, osVersion: string}}
   */
  getUserAgentDetails(userAgent) {
    if (!userAgent) {
      return {
        browserName: "Unknown",
        browserVersion: "Unknown",
        deviceVendor: "Unknown",
        deviceType: "Unknown",
        osName: "Unknown",
        osVersion: "Unknown",
      }
    }

    const parser = parse(userAgent)

    return {
      browserName: parser.browser || "Unknown",
      browserVersion: parser.browserVersion || "Unknown",
      deviceVendor: parser.deviceVendor || "Unknown",
      deviceType: parser.deviceType || "Unknown",
      osName: parser.os || "Unknown",
      osVersion: parser.osVersion || "Unknown",
    }
  }

  /**
   * Checks if user is enrolled and creates a login challenge
   *
   * @param {{ token: string, userId: string, ip: string, email: string, deviceId: string, userAgent: ?string }}
   * @returns {Promise<{ isValid: boolean, message: string, isEnroled: boolean }>}
   */
  async loginChallengeValidation({ token, userId, ip, email, deviceId, userAgent }) {
    const authsignal = this.client()
    const authUser = await authsignal.getUser({
      userId,
    })

    if (!authUser?.isEnrolled) {
      return { isValid: true, message: this.messages.UNKNOWN, isEnroled: false }
    }

    if (!token || !userId || !ip || !email || !deviceId) {
      return { isValid: false, message: this.messages.PARAMS_MISSING, isEnroled: true }
    }

    const trackRules = await this.#trackRules({
      ip,
      userId,
      email,
      deviceId,
      userAgent,
      action: this.actions.LOGIN,
    })

    if (trackRules) {
      return { isValid: false, message: this.messages.BLOCK }
    }

    const result = await authsignal.validateChallenge({
      token,
      userId,
    })

    if (!result?.isValid) {
      return { isValid: false, message: this.messages.UNAUTHORIZED }
    }

    return { isValid: true, message: this.messages.VALID, isEnroled: true }
  }

  /**
   * Enroll user to MFA
   *
   * @param {{ userId: string, email: string, deviceId: string, userAgent: ?string, ip: string }}
   * @returns {Promise<{ token: ?string, message: string, wasBlocked: boolean }>}
   */
  async enrollPasskey({ userId, email, ip, userAgent, deviceId }) {
    if (!userId || !email) {
      return {
        message: this.messages.PARAMS_MISSING,
        token: null,
        wasBlocked: true,
      }
    }

    const authsignal = this.client()
    const user = await authsignal.getUser({ userId })

    const trackRules = await this.#trackRules({
      ip,
      userId,
      email,
      deviceId,
      userAgent,
      action: this.actions.SINGUP,
    })

    if (trackRules) {
      return { isValid: false, message: this.messages.BLOCK, wasBlocked: true }
    }

    if (user?.isEnrolled) {
      return {
        message: this.messages.USER_ALREADY_ENROLLED,
        token: null,
        wasBlocked: false,
      }
    }

    const { token } = await authsignal.track({
      userId,
      action: this.actions.ENROL,
      attributes: { scope: "add:authenticators", email },
    })

    if (!token) {
      return { token: null, message: this.messages.MISSING_TOKEN, wasBlocked: true }
    }

    return { token, message: this.messages.SUCCESS, wasBlocked: false }
  }

  /**
   * Tracks if rules returned blocked or not
   *
   * @param {{ userId: string, email: string, deviceId: string, userAgent: ?string, ip: string, action: string }}
   * @returns {Promise<boolean>}
   */
  async #trackRules({ ip, userId, email, deviceId, userAgent, action }) {
    if (this.isMFAIgnored(ip)) return false

    const trackRules = await this.#client.track({
      action,
      userId,
      attributes: {
        ipAddress: ip,
        email,
        deviceId,
        userAgent,
        custom: this.getUserAgentDetails(userAgent),
      },
    })

    return trackRules?.state === this.messages.BLOCK
  }
}

export const Authsignal = new _AuthsignalService()
